import { media } from './product'

/**
 * GritVue X1 Quick Start Guide, step by step. Matches the printed guide,
 * which is the source of truth for charge time and runtime.
 */
export interface QuickStartStep {
  id: string
  short: string
  title: string
  body: string
  image: string
  imageAlt: string
  /** Optional list shown under the body (modes, lenses). */
  list?: { label: string; detail: string }[]
  tip?: string
}

export const quickStartSteps: QuickStartStep[] = [
  {
    id: 'power',
    short: 'Power button',
    title: 'Find the power button',
    body: 'The power button is on the left side of the glasses, right next to the LEDs.',
    image: media.x1Smoke,
    imageAlt: 'GritVue X1 showing the light module on the left side of the frame',
  },
  {
    id: 'on-off',
    short: 'On / off',
    title: 'Turn it on and off',
    body: 'Hold the power button for 2 seconds to turn the light on. Hold it for 2 seconds again to turn it off.',
    image: media.x1Smoke,
    imageAlt: 'GritVue X1 with the light on',
  },
  {
    id: 'modes',
    short: 'Brightness',
    title: 'Switch brightness modes',
    body: 'With the light on, press the power button to cycle through the modes.',
    image: media.x1Smoke,
    imageAlt: 'GritVue X1 light bar lit up',
    list: [
      { label: 'Low', detail: 'Press 1x' },
      { label: 'Medium', detail: 'Press 2x' },
      { label: 'High', detail: 'Press 3x' },
      { label: 'Off', detail: 'Press 4x' },
    ],
  },
  {
    id: 'wave',
    short: 'Motion sensor',
    title: 'Wave it on',
    body: 'Wave your hand in front of the sensor to turn the light on or off. No need to find the button with gloves or greasy hands.',
    image: media.x1Smoke,
    imageAlt: 'GritVue X1 light module with the motion sensor',
  },
  {
    id: 'charge',
    short: 'Charging',
    title: 'Charge it',
    body: 'Plug the included USB-C cable into the port on the X1 and into any USB power source. A full charge takes about 2.5 hours and gives about 3 hours 20 minutes of light.',
    list: [
      { label: 'Charge time', detail: 'About 2.5 hours' },
      { label: 'Runtime', detail: 'About 3h 20m' },
    ],
    image: media.cable,
    imageAlt: 'GritVue USB-C charging cable',
    tip: 'Make sure the USB port and the glasses are completely dry before you plug in the cable.',
  },
  {
    id: 'lenses',
    short: 'Swap lenses',
    title: 'Swap lenses, no tools',
    body: 'Clear, Amber and Smoke lenses are press-fit. Pop the lens out and press the next one in until it seats.',
    image: media.lensSet,
    imageAlt: 'GritVue X1 with the Clear, Amber and Smoke lenses',
    list: [
      { label: 'Clear', detail: 'Maximum clarity for indoor use and low light.' },
      { label: 'Amber', detail: 'More contrast in dust, haze and challenging light.' },
      { label: 'Smoke', detail: 'Less glare in bright outdoor conditions.' },
    ],
  },
  {
    id: 'care',
    short: 'Care',
    title: 'Keep it clear',
    body: 'Wipe the lenses with a microfiber cloth. When lenses fog up, use a quality anti-fog solution made for eyewear.',
    image: media.pouchCloth,
    imageAlt: 'GritVue protective pouch and cleaning cloth',
    tip: 'Store the X1 in its pouch or case so the lenses stay scratch-free.',
  },
]
